import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API, GOOGLE_CLIENT_ID, storeToken } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, Mail, Lock, User, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import DarkModeToggle from "../components/DarkModeToggle";
import { trackMetaEvent, setAdvancedMatching } from "../utils/metaTracking";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const Auth = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("reset_token");
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const googleProcessed = useRef(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Handle Google OAuth redirect callback — picks up ?code= from URL
  useEffect(() => {
    const code = searchParams.get("code");
    if (!code || googleProcessed.current) return;
    googleProcessed.current = true;

    const exchangeCode = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.post(`${API}/auth/google`, {
          code,
          redirect_uri: window.location.origin + "/auth",
        });
        if (response.data.user) {
          storeToken(response.data.session_token);
          toast.success(t("auth.loginSuccess"));
          // Clear the ?code= from URL
          window.history.replaceState(null, "", "/auth");
          if (response.data.is_new) {
            navigate("/health-assessment", { state: { user: response.data.user } });
          } else {
            navigate("/dashboard", { state: { user: response.data.user } });
          }
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Google login failed");
        // Clear the ?code= so user can retry
        window.history.replaceState(null, "", "/auth");
      } finally {
        setLoading(false);
      }
    };
    exchangeCode();
  }, [searchParams, navigate, t]);

  // Redirect to Google consent screen (works on all devices, no popups)
  const handleGoogleLogin = () => {
    const redirectUri = window.location.origin + "/auth";
    const scope = "openid email profile";
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=select_account`;
    window.location.href = url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email, password }
        : { email, password, name };

      const response = await axios.post(`${API}${endpoint}`, payload);

      if (response.data.user) {
        storeToken(response.data.session_token);
        // Advanced Matching — improves Pixel match-rate without needing CAPI token.
        setAdvancedMatching({
          email,
          first_name: (name || "").split(" ")[0],
          last_name: (name || "").split(" ").slice(1).join(" "),
          external_id: response.data.user.user_id,
        });
        toast.success(mode === "login" ? t("auth.loginSuccess") : t("auth.registerSuccess"));
        if (mode === "register") {
          trackMetaEvent(
            "CompleteRegistration",
            { content_name: "email_signup" },
            { email, first_name: (name || "").split(" ")[0], last_name: (name || "").split(" ").slice(1).join(" "), external_id: response.data.user.user_id },
          );
          navigate("/onboarding", { state: { user: response.data.user } });
        } else {
          navigate("/dashboard", { state: { user: response.data.user } });
        }
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError("Please enter your email"); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setResetSent(true);
      toast.success(t("auth.resetEmailSent"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success("Password reset successfully!");
      setMode("login");
      navigate("/auth", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sky-50 dark:bg-slate-900 flex items-center justify-center px-4" data-testid="auth-page">
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-sky-300/30 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-72 h-72 bg-indigo-300/20 rounded-full blur-3xl pointer-events-none" />

      <div className="absolute top-4 left-4">
        <Button variant="ghost" onClick={() => navigate("/")} className="text-sky-600 hover:text-sky-800" data-testid="back-to-landing-btn">
          <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
          {t("common.back")}
        </Button>
      </div>
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <DarkModeToggle />
        <LanguageSwitcher />
      </div>

      <div className="glass-card p-8 w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center glow-primary">
            <Sparkles className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-light text-sky-900 dark:text-sky-100" style={{ fontFamily: "Outfit" }}>
            {mode === "forgot" ? t("auth.forgotPassword") : mode === "reset" ? t("auth.resetPassword") : mode === "login" ? t("auth.welcomeBack") : t("auth.createAccount")}
          </h1>
        </div>

        {/* Password Reset Flow */}
        {mode === "forgot" && (
          resetSent ? (
            <div className="text-center space-y-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">{t("auth.resetEmailSent")}</p>
              <Button variant="ghost" onClick={() => { setMode("login"); setResetSent(false); }} className="text-sky-500" data-testid="back-to-login-btn">
                {t("auth.backToLogin")}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="reset-email" className="text-sky-700 dark:text-sky-300">{t("auth.email")}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="reset-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder={t("auth.emailPlaceholder")} className="pl-10" required data-testid="forgot-email-input" />
                </div>
              </div>
              {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600" data-testid="auth-error">{error}</div>}
              <Button type="submit" className="w-full glow-button py-5" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : t("auth.resetPassword")}
              </Button>
              <p className="text-center text-sm">
                <button type="button" onClick={() => setMode("login")} className="text-sky-500 hover:text-sky-700" data-testid="back-to-login-link">{t("auth.backToLogin")}</button>
              </p>
            </form>
          )
        )}

        {/* Reset with Token */}
        {mode === "reset" && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new-password" className="text-sky-700 dark:text-sky-300">{t("auth.newPassword")}</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters" className="pl-10" required data-testid="new-password-input" />
              </div>
            </div>
            {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600" data-testid="auth-error">{error}</div>}
            <Button type="submit" className="w-full glow-button py-5" disabled={loading} data-testid="reset-submit-btn">
              {loading ? <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : t("auth.confirmReset")}
            </Button>
          </form>
        )}

        {/* Login/Register Form */}
        {(mode === "login" || mode === "register") && (
          <>
            <Button onClick={handleGoogleLogin} variant="outline" className="w-full mb-6 py-5 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-slate-600 hover:bg-sky-50 dark:hover:bg-slate-800" data-testid="google-login-btn">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t("auth.continueWithGoogle")}
            </Button>

            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px bg-sky-200 dark:bg-slate-600" />
              <span className="text-xs text-slate-400 uppercase">{t("auth.orEmail")}</span>
              <div className="flex-1 h-px bg-sky-200 dark:bg-slate-600" />
            </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="name" className="text-sky-700">{t("auth.name")}</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("auth.namePlaceholder")}
                  className="pl-10"
                  data-testid="name-input"
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="email" className="text-sky-700">{t("auth.email")}</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                className="pl-10"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-sky-700">{t("auth.password")}</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? t("auth.passwordMin") : t("auth.passwordPlaceholder")}
                className="pl-10 pr-10"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600" data-testid="auth-error">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full glow-button py-5" disabled={loading} data-testid="submit-btn">
            {loading ? (
              <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              mode === "login" ? t("auth.login") : t("auth.register")
            )}
          </Button>
        </form>

        {mode === "login" && (
          <p className="text-center text-sm mt-3">
            <button onClick={() => { setMode("forgot"); setError(""); }} className="text-sky-400 hover:text-sky-600 text-xs" data-testid="forgot-password-link">
              {t("auth.forgotPassword")}
            </button>
          </p>
        )}

        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
          {mode === "login" ? t("auth.noAccount") : t("auth.hasAccount")}{" "}
          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-sky-500 hover:text-sky-700 font-medium"
            data-testid="toggle-mode-btn"
          >
            {mode === "login" ? t("auth.register") : t("auth.login")}
          </button>
        </p>

        {/* Contact link */}
        <div className="text-center mt-6 pt-4 border-t border-sky-100 dark:border-slate-700">
          <a
            href="/contact"
            className="text-sky-400 hover:text-sky-600 text-xs inline-flex items-center gap-1.5 transition-colors"
            data-testid="auth-contact-link"
          >
            <Mail className="w-3.5 h-3.5" /> Need help? Contact us
          </a>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Auth;
